import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Users, FilePlus, Files, LogOut, Inbox, FolderPlus, FolderKanban, LayoutDashboard, MailOpen, UserPlus, Images, Contact, MailPlus, MessagesSquare, ShieldCheck, HelpCircle, UserCircle2, KeyRound, ChevronsUpDown, ShieldAlert, ArrowLeft, Settings, Users2, BadgeCheck, Map, PieChart, HardHat } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { UserAvatar } from "@/components/shared";
import { hasPerm } from "@/lib/perms";
import { isImpersonating, endImpersonation } from "@/lib/impersonate";
import { Button } from "@/components/ui/button";

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "superadmin";
  const isCustomer = user?.role === "customer";
  const profileBase = isAdmin ? "/admin" : isCustomer ? "/musteri" : "/tedarikci";
  const [unread, setUnread] = useState(0);
  const [newOffers, setNewOffers] = useState(0);
  const [proposalAlerts, setProposalAlerts] = useState(0);
  const prevAlerts = useRef(null);
  const impersonating = isImpersonating() && !isAdmin;

  useEffect(() => {
    if (!isAdmin) return;
    const load = () => {
      if (hasPerm(user, "sohbetler")) api.get("/chats/unread-count").then((r) => setUnread(r.data.total)).catch(() => {});
      if (hasPerm(user, "teklifler")) {
        api.get("/offers/unseen-count").then((r) => setNewOffers(r.data.count)).catch(() => {});
        api.get("/price-proposals/pending-count").then((r) => {
          const c = r.data.count;
          setProposalAlerts(c);
          if (prevAlerts.current !== null && c > prevAlerts.current) {
            toast.warning("Yeni pazarlık yanıtı geldi! Bir tedarikçi karşı teklif verdi veya öneriyi kabul etti.", { duration: 8000 });
          }
          prevAlerts.current = c;
        }).catch(() => {});
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [isAdmin, location.pathname]);

  const items = isAdmin
    ? [
        { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, tid: "nav-dashboard", perm: "dashboard" },
        { to: "/admin/ozet", label: "Yönetici Özeti", icon: PieChart, tid: "nav-summary", perm: "dashboard" },
        { to: "/admin/kurulum", label: "Setup Dashboard", icon: HardHat, tid: "nav-setup-dashboard", perm: "projeler" },
        { to: "/saha-fotograflari", label: "Saha Fotoğrafları", icon: Images, tid: "nav-field-photos", perm: "projeler" },
        { to: "/admin/sohbetler", label: "Sohbetler", icon: MessagesSquare, tid: "nav-chats", badge: unread, perm: "sohbetler" },
        { to: "/admin/kullanicilar", label: "Tedarikçiler", icon: Users, tid: "nav-users", perm: "tedarikciler" },
        { to: "/admin/davetler", label: "Davetler", icon: MailPlus, tid: "nav-invites", perm: "davetler" },
        { to: "/admin/musteriler", label: "Müşteriler", icon: Contact, tid: "nav-customers", perm: "musteriler" },
        { to: "/admin/proje-olustur", label: "Proje Oluştur", icon: FolderPlus, tid: "nav-create-project", perm: "projeler" },
        { to: "/admin/projeler", label: "Tüm Projeler", icon: FolderKanban, tid: "nav-projects", perm: "projeler" },
        { to: "/admin/planlar", label: "Planlar", icon: Map, tid: "nav-plans", perm: "projeler" },
        { to: "/admin/teklif-al", label: "Teklif Al", icon: FilePlus, tid: "nav-quote-request", perm: "teklifler" },
        { to: "/admin/teklifler", label: "Tüm Teklifler", icon: Files, tid: "nav-requests", perm: "teklifler", badge: newOffers + proposalAlerts },
        { to: "/admin/teklif-onayi", label: "Teklif Onayı", icon: BadgeCheck, tid: "nav-approvals", perm: "teklifler" },
        { to: "/admin/basvurular", label: "Başvurular", icon: MailOpen, tid: "nav-leads", perm: "basvurular" },
        { to: "/admin/tedarikci-basvurulari", label: "Tedarikçi Başvuruları", icon: UserPlus, tid: "nav-supplier-apps", perm: "basvurular" },
        { to: "/admin/referanslar", label: "Referanslar", icon: Images, tid: "nav-references", perm: "referanslar" },
        { to: "/admin/sss", label: "SSS", icon: HelpCircle, tid: "nav-faq", perm: "sss" },
        { to: "/admin/takim", label: "Takım", icon: Users2, tid: "nav-team", perm: "teklifler" },
        { to: "/admin/yoneticiler", label: "Kullanıcılar", icon: ShieldCheck, tid: "nav-admin-users", perm: "kullanicilar" },
        { to: "/admin/ayarlar", label: "Ayarlar", icon: Settings, tid: "nav-settings", perm: "teklifler" },
      ].filter((it) => hasPerm(user, it.perm))
    : isCustomer
    ? [
        { to: "/musteri/dashboard", label: "Dashboard", icon: LayoutDashboard, tid: "nav-customer-dashboard" },
        { to: "/musteri/projeler", label: "Projelerim", icon: FolderKanban, tid: "nav-customer-projects" },
        { to: "/musteri/proje-olustur", label: "Yeni Proje", icon: FolderPlus, tid: "nav-customer-create" },
        { to: "/musteri/teklif-al", label: "Teklif Al", icon: FilePlus, tid: "nav-customer-quote-request" },
        { to: "/musteri/teklifler", label: "Tekliflerim", icon: Files, tid: "nav-customer-requests" },
        { to: "/musteri/sss", label: "SSS", icon: HelpCircle, tid: "nav-customer-faq" },
      ]
    : [
        { to: "/tedarikci/dashboard", label: "Dashboard", icon: LayoutDashboard, tid: "nav-supplier-dashboard" },
        { to: "/tedarikci/talepler", label: "Teklif Talepleri", icon: Inbox, tid: "nav-supplier-requests" },
        { to: "/tedarikci/sss", label: "SSS", icon: HelpCircle, tid: "nav-supplier-faq" },
      ];

  return (
    <div className="min-h-screen bg-[#F5F7FA] text-slate-900">
      {impersonating && (
        <div className="fixed top-0 inset-x-0 z-50 bg-amber-400 text-slate-900 px-4 h-10 flex items-center justify-center gap-3 text-sm font-medium" data-testid="impersonation-banner">
          <ShieldAlert size={15} className="shrink-0" />
          <span className="truncate">Şu an <strong>{user?.company_name || user?.username}</strong> hesabındasınız</span>
          <Button
            size="sm"
            variant="outline"
            onClick={endImpersonation}
            data-testid="end-impersonation-button"
            className="rounded-none h-7 border-slate-900/30 bg-white/80 hover:bg-white gap-1.5 text-xs font-semibold"
          >
            <ArrowLeft size={12} /> Admin'e Dön
          </Button>
        </div>
      )}
      {impersonating && <div className="h-10" />}
      <aside className="hidden md:flex w-64 shrink-0 border-r border-slate-200 flex-col fixed inset-y-0 left-0 bg-[#F5F7FA] z-20" style={impersonating ? { top: "40px" } : undefined}>
        <div className="px-6 py-7 border-b border-slate-200">
          <div className="font-heading font-extrabold text-2xl tracking-tighter">Gett<span className="text-slate-400">Proposal</span></div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-1">{isCustomer ? "Müşteri Paneli" : "Tedarikçi Portalı"}</div>
        </div>
        <nav className="flex-1 overflow-y-auto py-6 px-3 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.tid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors duration-200 ${
                  isActive ? "bg-slate-900 text-white font-semibold" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                }`
              }
            >
              <it.icon size={17} />
              {it.label}
              {it.badge > 0 && (
                <span className="ml-auto bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 leading-none rounded-full" data-testid={`${it.tid}-badge`}>{it.badge}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-4 space-y-3 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="w-full text-left flex items-center gap-2 hover:bg-slate-100 -mx-1 px-1 py-1 transition-colors duration-150" data-testid="profile-menu-trigger">
                <UserAvatar userId={user?.id} avatarId={user?.avatar_file_id} name={user?.contact_name || user?.company_name || user?.username} size={34} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate" data-testid="sidebar-username">{user?.username}</div>
                  <div className="text-xs text-slate-500 truncate">{isAdmin ? "Süper Admin" : user?.company_name}</div>
                </div>
                <ChevronsUpDown size={14} className="text-slate-400 shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="rounded-none w-52">
              <DropdownMenuItem onClick={() => navigate(`${profileBase}/profil`)} data-testid="menu-profile" className="rounded-none gap-2 text-sm cursor-pointer">
                <UserCircle2 size={14} /> Profilim
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`${profileBase}/profil?tab=sifre`)} data-testid="menu-password" className="rounded-none gap-2 text-sm cursor-pointer">
                <KeyRound size={14} /> Şifre İşlemleri
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            size="sm"
            className="w-full rounded-none border-slate-200 hover:bg-slate-100 gap-2 justify-start"
            onClick={() => {
              logout();
              navigate("/login");
            }}
            data-testid="logout-button"
          >
            <LogOut size={14} /> Çıkış Yap
          </Button>
        </div>
      </aside>
      <header className="md:hidden sticky z-30 bg-[#F5F7FA] border-b border-slate-200" style={{ top: impersonating ? "40px" : 0 }}>
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <div className="font-heading font-extrabold text-lg tracking-tighter">Gett<span className="text-slate-400">Proposal</span></div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500">{isCustomer ? "Müşteri Paneli" : "Tedarikçi Portalı"}</div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-none border-slate-200 hover:bg-slate-100 h-9 px-2.5"
            onClick={() => navigate(`${profileBase}/profil`)}
            data-testid="mobile-profile-button"
            title="Profilim"
          >
            <UserCircle2 size={15} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-none border-slate-200 hover:bg-slate-100 gap-1.5 h-9"
            onClick={() => { logout(); navigate("/login"); }}
            data-testid="mobile-logout-button"
          >
            <LogOut size={13} /> Çıkış
          </Button>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-2" data-testid="mobile-nav">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`mobile-${it.tid}`}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-2 text-xs whitespace-nowrap shrink-0 transition-colors duration-200 ${
                  isActive ? "bg-slate-900 text-white font-semibold" : "text-slate-500 bg-white border border-slate-200"
                }`
              }
            >
              <it.icon size={13} />
              {it.label}
              {it.badge > 0 && (
                <span className="bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 leading-none rounded-full">{it.badge}</span>
              )}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="md:ml-64 p-4 sm:p-6 md:p-8 lg:p-12">
        <Outlet />
      </main>
    </div>
  );
}
